import i18n from "@/i18n";
import { apiFetcher } from "@/services/API/API.service";
import { NewChatMessageResponse, newChatMessageResponseSchema } from "@/types";
import { validateWithSchema } from "@/utils/dataValidators.zod";

// #region fetchMessageHistory
export async function fetchMessageHistory({
  conversationID,
  signal,
  retry = false,
}: {
  conversationID: number;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      rData: NewChatMessageResponse;
    }
  | {
      rStatus: "error";
      rMessage: string;
    }
> {
  // FOR TESTING PURPOSES ONLY
  // if (conversationID) {
  //   return {
  //     rStatus: "success",
  //     rData: {
  //       conversation: {
  //         id: conversationID,
  //         name: "Sample Conversation",
  //         agentID: 1,
  //         last_date: new Date().toISOString(),
  //       },
  //       messages: [
  //         {
  //           id: 1,
  //           sender: "agent",
  //           content:
  //             '# Markdown Features Showcase\r\n\r\nThis document demonstrates a wide range of features supported by Markdown.\r\n\r\n## 1. Headings\r\n\r\nHeadings are used to structure your document. They range from level 1 to level 6.\r\n\r\n# Heading 1\r\n\r\n## Heading 2\r\n\r\n### Heading 3\r\n\r\n#### Heading 4\r\n\r\n##### Heading 5\r\n\r\n###### Heading 6\r\n\r\nYou can also use underline-style headings for level 1 and 2.\r\n\r\n# Alt-H1\r\n\r\n## Alt-H2\r\n\r\n## 2. Paragraphs and Line Breaks\r\n\r\nThis is a paragraph. Paragraphs are one or more consecutive lines of text, separated by one or more blank lines.\r\n\r\nTo create a line break (a new line without a paragraph), end a line with two or more spaces, and then type return.\r\nThis is the first line.  \r\nThis is the second line (notice the two spaces before the line break).\r\n\r\n## 3. Emphasis\r\n\r\nYou can emphasize text by making it _italic_ or **bold**.\r\n\r\n- _Italic_: Surround text with one asterisk `*` or one underscore `_`.\r\n  - Example: _This is italic text_ or _This is also italic_.\r\n- **Bold**: Surround text with two asterisks `**` or two underscores `__`.\r\n  - Example: **This is bold text** or **This is also bold**.\r\n- **_Bold and Italic_**: Combine three asterisks `***` or three underscores `___`.\r\n  - Example: **_This is bold and italic text_** or **_This is also bold and italic_**.\r\n\r\n## 4. Blockquotes\r\n\r\nBlockquotes are used to indicate text quoted from another source.\r\n\r\n> This is a blockquote.\r\n> It can span multiple lines.\r\n>\r\n> > You can nest blockquotes.\r\n> > This is a nested blockquote.\r\n\r\n## 5. Lists\r\n\r\n### Unordered Lists\r\n\r\nUse `-`, `+`, or `*` for unordered lists.\r\n\r\n- Item 1\r\n- Item 2\r\n  - Subitem 2.1 (indent with two spaces)\r\n  - Subitem 2.2\r\n- Item 3\r\n\r\n### Ordered Lists\r\n\r\nUse numbers followed by a period for ordered lists.\r\n\r\n1. First item\r\n2. Second item\r\n3. Third item\r\n   1. Subitem 3.1 (indent with three spaces)\r\n   2. Subitem 3.2\r\n4. Fourth item\r\n\r\nThe actual numbers you use to mark the list don\'t matter. Markdown will render them as a sequential list.\r\n\r\n1. Apple\r\n1. Banana\r\n1. Cherry\r\n\r\n### Task Lists (GitHub Flavored Markdown)\r\n\r\n- [x] Finish the report\r\n- [ ] Respond to emails\r\n- [ ] Call the client\r\n\r\n## 6. Code\r\n\r\n### Inline Code\r\n\r\nWrap inline code snippets in backticks `` ` ``.\r\n\r\nFor example, `console.log(\'Hello, Markdown!\');` is a JavaScript statement.\r\n\r\n### Code Blocks\r\n\r\nTo create a code block, indent every line of the block by at least four spaces or one tab.\r\n\r\n    <html>\r\n      <head>\r\n        <title>Page Title</title>\r\n      </head>\r\n      <body>\r\n        <h1>This is a Heading</h1>\r\n        <p>This is a paragraph.</p>\r\n      </body>\r\n    </html>\r\n\r\nAlternatively, you can use "fenced" code blocks with three backticks ` ``` ` or three tildes `~~~`. This is often preferred as it allows for syntax highlighting.\r\n\r\n#### JavaScript Example\r\n\r\n```javascript\r\nfunction greet(name) {\r\n  console.log(`Hello, ${name}!`);\r\n}\r\n\r\ngreet("World");\r\n```\r\n\r\n#### Python Example\r\n\r\n```python\r\ndef fibonacci(n):\r\n    if n <= 1:\r\n        return n\r\n    else:\r\n        return fibonacci(n-1) + fibonacci(n-2)\r\n\r\n# Print the first 10 Fibonacci numbers\r\nfor i in range(10):\r\n    print(fibonacci(i))\r\n```\r\n\r\n#### HTML Example\r\n\r\n```html\r\n<!DOCTYPE html>\r\n<html lang="en">\r\n  <head>\r\n    <meta charset="UTF-8" />\r\n    <meta name="viewport" content="width=device-width, initial-scale=1.0" />\r\n    <title>Markdown Example</title>\r\n  </head>\r\n  <body>\r\n    <h1>Welcome to Markdown</h1>\r\n    <p>This is a simple HTML page.</p>\r\n  </body>\r\n</html>\r\n```\r\n\r\n## 7. Horizontal Rules\r\n\r\nYou can create a horizontal rule with three or more hyphens, asterisks, or underscores.\r\n\r\n---\r\n\r\n---\r\n\r\n---\r\n\r\n## 8. Links\r\n\r\n### Inline Links\r\n\r\n[Markdown Guide](https://www.markdownguide.org/)\r\n\r\n### Links with Titles\r\n\r\n[Markdown Guide](https://www.markdownguide.org/ "The best Markdown guide!")\r\n\r\n### Reference-style Links\r\n\r\nYou can also use reference-style links.\r\n\r\nI get 10 times more traffic from [Google][1] than from [Yahoo][2] or [MSN][3].\r\n\r\n[1]: http://google.com/ "Google"\r\n[2]: http://search.yahoo.com/ "Yahoo Search"\r\n[3]: http://search.msn.com/ "MSN Search"\r\n\r\n### Email Links\r\n\r\n<email@example.com>\r\n\r\n## 9. Images\r\n\r\n### Inline Images\r\n\r\n![Markdown Logo](https://markdown-here.com/img/icon256.png)\r\n\r\n### Images with Titles\r\n\r\n![Markdown Logo](https://markdown-here.com/img/icon256.png "Markdown Logo")\r\n\r\n### Reference-style Images\r\n\r\n![Markdown Logo][logo]\r\n\r\n[logo]: https://markdown-here.com/img/icon256.png "Markdown Logo"\r\n\r\n## 10. Tables (GitHub Flavored Markdown)\r\n\r\nTables are created using pipes `|` and hyphens `-`.\r\n\r\n| Align Left | Align Center | Align Right |\r\n| :--------- | :----------: | ----------: |\r\n| Cell 1     |    Cell 2    |      Cell 3 |\r\n| Left       |    Center    |       Right |\r\n| Data       |     Data     |        Data |\r\n\r\nYou can also include Markdown formatting inside table cells.\r\n\r\n| Name     | Description                  | Syntax                   |\r\n| -------- | ---------------------------- | ------------------------ |\r\n| **Bold** | Makes text bold.             | `**text**` or `__text__` |\r\n| _Italic_ | Makes text italic.           | `*text*` or `_text_`     |\r\n| `Code`   | Formats text as inline code. | `` `code` ``             |\r\n\r\n## 11. Footnotes (GitHub Flavored Markdown)\r\n\r\nFootnotes allow you to add notes and references without cluttering the body of the document.\r\n\r\nHere\'s a sentence with a footnote.[^1]\r\n\r\n[^1]: This is the footnote.\r\n\r\nYou can also have multiple footnotes.\r\n\r\nAnother sentence with a footnote.[^2]\r\n\r\n[^2]: This is the second footnote.\r\n\r\n## 12. Strikethrough (GitHub Flavored Markdown)\r\n\r\nYou can strikethrough text by wrapping it with two tildes `~~`.\r\n\r\n~~This text is crossed out.~~\r\n\r\n## 13. Emoji (GitHub Flavored Markdown)\r\n\r\nYou can add emoji to your Markdown by typing the emoji code surrounded by colons.\r\n\r\n- :smile:\r\n- :rocket:\r\n- :heart:\r\n- :+1:\r\n- :octocat:\r\n\r\n## 14. Automatic URL Linking (GitHub Flavored Markdown)\r\n\r\nMany Markdown processors automatically turn URLs into links, even if you don\'t use the link syntax.\r\n\r\nhttps://www.markdownguide.org\r\n\r\n## 15. Disabling Automatic URL Linking\r\n\r\nIf you want to display a URL that is not a link, you can enclose it in backticks.\r\n\r\n`https://www.markdownguide.org`\r\n\r\n## 16. HTML in Markdown\r\n\r\nYou can use raw HTML in Markdown documents. This is useful for features that don\'t have a direct Markdown equivalent, like advanced formatting or embedding content.\r\n\r\n<dl>\r\n  <dt>Definition List Title</dt>\r\n  <dd>Definition list division.</dd>\r\n  <dt>Ubuntu</dt>\r\n  <dd>A Debian-based Linux operating system.</dd>\r\n  <dt>Windows</dt>\r\n  <dd>A proprietary graphical operating system developed by Microsoft.</dd>\r\n</dl>\r\n\r\nYou can also use HTML for more complex tables or for attributes not supported by Markdown\'s table syntax.\r\n\r\n<table>\r\n  <tr>\r\n    <th colspan="2">HTML Table</th>\r\n  </tr>\r\n  <tr>\r\n    <td>Cell 1</td>\r\n    <td>Cell 2</td>\r\n  </tr>\r\n</table>\r\n\r\n## 17. Escaping Characters\r\n\r\nIf you want to use a Markdown character as a literal character, you can escape it with a backslash `\\`.\r\n\r\n\\* This is not an italicized line, it\'s just text with asterisks. \\*\r\n\\# This is not a heading.\r\n\\[This is not a link\\](https://www.example.com)\r\n\r\nCharacters you can escape:\r\n\r\n- `\\` backslash\r\n- `` ` `` backtick\r\n- `*` asterisk\r\n- `_` underscore\r\n- `{` `}` curly braces\r\n- `[` `]` square brackets\r\n- `(` `)` parentheses\r\n- `#` hash mark\r\n- `+` plus sign\r\n- `-` minus sign (hyphen)\r\n- `.` dot\r\n- `!` exclamation mark\r\n- `|` pipe\r\n\r\n---\r\n\r\nThis concludes the Markdown features showcase.\r\n',
  //         },
  //       ],
  //     },
  //   };
  // }
  if (!conversationID) {
    console.error("conversationID is required");
    return {
      rStatus: "error",
      rMessage: i18n.t("conversationID is required"),
    };
  }

  if (retry) {
    console.log("Retrying fetchMessageHistory");
  }

  try {
    const response = await apiFetcher({
      route: "/chat/history/single",
      method: "POST",
      signal: signal,
      options: {
        body: {
          conversationID: conversationID,
        },
      },
    });

    console.log("response", response);
    if (response.status === 200) {
      const validatedData = validateWithSchema<NewChatMessageResponse>(
        response.data,
        newChatMessageResponseSchema,
        "NewChatMessageResponse"
      );

      if (!validatedData) {
        console.error(
          "Invalid response: data is not an array of valid chat messages",
          response.data
        );
        return {
          rStatus: "error",
          rMessage: i18n.t("Invalid response data"),
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }

    if (response.status === 204) {
      console.error("Chat not found");
      return {
        rStatus: "error",
        rMessage: i18n.t("Chat not found"),
      };
    }

    if (response.status === 400) {
      console.error("Bad request");
      return { rStatus: "error", rMessage: i18n.t("Bad request") };
    }

    if (response.status === 401 || response.status === 403) {
      console.error("Unauthorized");
      return { rStatus: "error", rMessage: i18n.t("Unauthorized") };
    }

    if (response.status === 404) {
      console.error("Route not found");
      return { rStatus: "error", rMessage: i18n.t("Route not found") };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", rMessage: "request_aborted" };
      }

      if (!retry) {
        return fetchMessageHistory({ conversationID, signal, retry: true });
      }

      console.error("Network error");
      return { rStatus: "error", rMessage: i18n.t("Network error") };
    }

    console.error("Unexpected error: ", response);
    return { rStatus: "error", rMessage: i18n.t("Failed to fetch") };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: i18n.t("Failed to fetch") };
  }
}
// #endregion fetchMessageHistory

// #region sendMessageToChat
export async function sendMessageToChat({
  conversationID,
  message,
  signal,
  modelID,
}: {
  conversationID: number;
  modelID: number;
  message: string;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      rData: NewChatMessageResponse;
    }
  | {
      rStatus: "error";
      rMessage: string;
    }
> {
  if (!conversationID) {
    console.error("conversationID is required");
    return {
      rStatus: "error",
      rMessage: i18n.t("conversationID is required"),
    };
  }

  if (!message) {
    console.error("message is required");
    return {
      rStatus: "error",
      rMessage: i18n.t("message is required"),
    };
  }

  try {
    const response = await apiFetcher({
      route: "/chat/message/send",
      method: "POST",
      signal: signal,
      options: {
        body: {
          conversationID: conversationID,
          modelID: modelID,
          message: message,
        },
      },
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema<NewChatMessageResponse>(
        response.data,
        newChatMessageResponseSchema,
        "NewChatMessageResponse"
      );

      if (!validatedData) {
        console.error(
          "Invalid response: data is not a valid chat message object",
          response.data
        );
        return {
          rStatus: "error",
          rMessage: i18n.t("Invalid response data"),
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }

    if (response.status === 400) {
      console.error("Bad request");
      return { rStatus: "error", rMessage: i18n.t("Bad request") };
    }

    if (response.status === 401 || response.status === 403) {
      console.error("Unauthorized");
      return { rStatus: "error", rMessage: i18n.t("Unauthorized") };
    }

    if (response.status === 404) {
      console.error("Route not found");
      return { rStatus: "error", rMessage: i18n.t("Route not found") };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", rMessage: "request_aborted" };
      }
      console.error("Network error");
      return { rStatus: "error", rMessage: i18n.t("Network error") };
    }

    console.error("Unexpected error: ", response);
    return { rStatus: "error", rMessage: i18n.t("Failed to send message") };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: i18n.t("Failed to send message") };
  }
}
// #endregion sendMessageToChat

// #region sendNewChatMessage
export async function sendNewChatMessage({
  message,
  signal,
  modelID,
}: {
  modelID: number;
  message: string;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      rData: NewChatMessageResponse;
    }
  | {
      rStatus: "error";
      rMessage: string;
    }
> {
  if (!message) {
    console.error("message is required");
    return {
      rStatus: "error",
      rMessage: i18n.t("message is required"),
    };
  }

  if (!modelID) {
    console.error("modelID is required");
    return {
      rStatus: "error",
      rMessage: i18n.t("modelID is required"),
    };
  }

  try {
    const response = await apiFetcher({
      route: "/chat/new",
      method: "POST",
      signal: signal,
      options: {
        body: {
          agentID: modelID,
          message: message,
        },
      },
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema<NewChatMessageResponse>(
        response.data,
        newChatMessageResponseSchema,
        "NewChatMessageResponse"
      );

      if (!validatedData) {
        console.error(
          "Invalid response: data is not a valid chat message object",
          response.data
        );
        return {
          rStatus: "error",
          rMessage: i18n.t("Invalid response data"),
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }

    if (response.status === 400) {
      console.error("Bad request");
      return { rStatus: "error", rMessage: i18n.t("Bad request") };
    }

    if (response.status === 401 || response.status === 403) {
      console.error("Unauthorized");
      return { rStatus: "error", rMessage: i18n.t("Unauthorized") };
    }

    if (response.status === 404) {
      console.error("Route not found");
      return { rStatus: "error", rMessage: i18n.t("Route not found") };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", rMessage: "request_aborted" };
      }
      console.error("Network error");
      return { rStatus: "error", rMessage: i18n.t("Network error") };
    }

    console.error("Unexpected error: ", response);
    return { rStatus: "error", rMessage: i18n.t("Failed to send message") };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: i18n.t("Failed to send message") };
  }
}
// #endregion sendNewChatMessage

// #region pingChatForNewMessages
export async function pingChatForNewMessages({
  conversationID,
  signal,
}: {
  conversationID: number;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      rData: NewChatMessageResponse;
    }
  | {
      rStatus: "error";
      rMessage:
        | "conversation_id"
        | "request_aborted"
        | "pending"
        | "unauthorized"
        | "invalid_data"
        | "pending_not_found"
        | "route_not_found"
        | "network_error"
        | "bad_request"
        | "internal_server_error"
        | "failed_to_ping_chat";
    }
> {
  if (!conversationID) {
    console.error("conversationID is required");
    return {
      rStatus: "error",
      rMessage: "conversation_id",
    };
  }

  try {
    const response = await apiFetcher({
      route: "/chat/check/status",
      method: "POST",
      signal: signal,
      options: {
        body: {
          conversationID: conversationID,
        },
      },
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema<NewChatMessageResponse>(
        response.data,
        newChatMessageResponseSchema,
        "NewChatMessageResponse"
      );

      if (!validatedData) {
        console.error(
          "Invalid response: data is not a valid chat message object",
          response.data
        );
        return {
          rStatus: "error",
          rMessage: "invalid_data",
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }
    if (response.status === 202) {
      return { rStatus: "error", rMessage: "pending" };
    }

    if (response.status === 204) {
      return { rStatus: "error", rMessage: "pending_not_found" };
    }

    if (response.status === 400) {
      console.error("Bad request");
      return {
        rStatus: "error",
        rMessage: "bad_request",
      };
    }

    if (response.status === 401 || response.status === 403) {
      console.error("Unauthorized");
      return {
        rStatus: "error",
        rMessage: "unauthorized",
      };
    }

    if (response.status === 404) {
      console.error("Route not found");
      return {
        rStatus: "error",
        rMessage: "route_not_found",
      };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", rMessage: "request_aborted" };
      }
      console.error("Network error");
      return {
        rStatus: "error",
        rMessage: "network_error",
      };
    }

    console.error("Unexpected error: ", response);
    return { rStatus: "error", rMessage: "failed_to_ping_chat" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: "failed_to_ping_chat" };
  }
}
// #endregion pingChatForNewMessages
